import { useCallback } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { ProductCard } from "./ProductCard";
import { CATEGORIES } from "../../data/products";

const CategoryRow = ({ category, products, onAdd }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true,
  });
  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  if (!products || products.length === 0) return null;

  return (
    <div
      id={`cat-${category.slug}`}
      className="py-[60px] md:py-[84px] border-t border-neutral-200"
      data-testid={`category-${category.slug}`}
    >
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">
              Category · {products.length} products
            </div>
            <h3 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-none">
              {category.name}
              <span className="font-thin"> .</span>
            </h3>
            <p className="mt-3 text-sm md:text-base text-neutral-600 font-light">
              {category.tagline}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              className="w-10 h-10 border border-neutral-300 hover:border-black hover:bg-black hover:text-white transition-colors flex items-center justify-center"
              data-testid={`prev-${category.slug}`}
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={next}
              className="w-10 h-10 border border-neutral-300 hover:border-black hover:bg-black hover:text-white transition-colors flex items-center justify-center"
              data-testid={`next-${category.slug}`}
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <Link
              to={`/category/${category.slug}`}
              className="group ml-2 inline-flex items-center gap-2 text-sm font-medium border-b border-black pb-1 hover:gap-3 transition-all"
              data-testid={`view-all-${category.slug}`}
            >
              View all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="overflow-hidden border-l border-t border-neutral-200" ref={emblaRef}>
          <div className="flex">
            {products.map((p) => (
              <div
                key={p.id}
                className="flex-[0_0_75%] sm:flex-[0_0_45%] md:flex-[0_0_30%] lg:flex-[0_0_23%] min-w-0"
              >
                <ProductCard product={p} onAdd={onAdd} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const CategoryShowcase = ({ catalog = [], onAdd }) => {
  // Use catalog from API, fallback to static CATEGORIES order if catalog empty
  const rows = catalog.length > 0 ? catalog : CATEGORIES.map((c) => ({ ...c, products: [] }));
  return (
    <section className="bg-white" data-testid="categories-section">
      {rows.map((c) => (
        <CategoryRow key={c.slug} category={c} products={c.products || []} onAdd={onAdd} />
      ))}
    </section>
  );
};
